import React, { useEffect, useState, useCallback } from 'react';
import NutritionCalendar from '../components/NutritionCalendar';
import MealForm from '../components/MealForm';
import DayDetail from '../components/DayDetail';
import { mealsAPI } from '../api/client';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

export default function Dashboard() {
  const today = new Date();
  const [year, setYear]               = useState(today.getFullYear());
  const [month, setMonth]             = useState(today.getMonth() + 1);
  const [logs, setLogs]               = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayMeals, setDayMeals]       = useState([]);
  const [dayLog, setDayLog]           = useState(null);
  const [loading, setLoading]         = useState(true);

  const todayStr = today.toISOString().slice(0, 10);

  const getGreeting = () => {
    const h = today.getHours();
    if (h < 12) return '☀️ Good morning';
    if (h < 17) return '🌤 Good afternoon';
    return '🌙 Good evening';
  };

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mealsAPI.getCalendar(year, month);
      setLogs(res.data.logs);
    } catch (err) {
      console.error('Failed to load calendar', err);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const handleChangeMonth = (delta) => {
    let newMonth = month + delta;
    let newYear  = year;
    if (newMonth < 1)  { newMonth = 12; newYear -= 1; }
    else if (newMonth > 12) { newMonth = 1; newYear += 1; }
    setMonth(newMonth);
    setYear(newYear);
    setSelectedDate(null);
  };

  const loadDay = async (dateStr) => {
    setSelectedDate(dateStr);
    try {
      const res = await mealsAPI.getDay(dateStr);
      setDayMeals(res.data.meals);
      setDayLog(res.data.dailyLog);
    } catch (err) {
      console.error('Failed to load day', err);
    }
  };

  const handleMealAdded = ({ meal, dailyLog }) => {
    setDayMeals((prev) => {
      const others = prev.filter((m) => m._id !== meal._id);
      return [...others, meal];
    });
    setDayLog(dailyLog);
    loadCalendar();
  };

  const handleMealDeleted = (mealId, updatedLog) => {
    setDayMeals((prev) => prev.filter((m) => m._id !== mealId));
    setDayLog(updatedLog);
    loadCalendar();
  };

  /* ── Stats ── */
  const loggedDays    = logs.length;
  const totalCalories = logs.reduce((s, l) => s + (l.totalCalories || 0), 0);
  const avgCalories   = loggedDays > 0 ? Math.round(totalCalories / loggedDays) : 0;
  const healthyDays   = logs.filter(l =>
    ['dark_green', 'green', 'light_green'].includes(l.colorRating)
  ).length;

  /* ── Today's log shortcut ── */
  const todayLog = logs.find(l => l.date === todayStr);
  const isTodayInView = year === today.getFullYear() && month === (today.getMonth() + 1);

  return (
    <div className="container" style={{ animationDelay: '0s' }}>

      {/* ── Hero Greeting ── */}
      <div className="dash-hero animate-in">
        <p className="dash-greeting">{getGreeting()}</p>
        <h1 className="dash-title">Your nutrition calendar</h1>
        <p className="dash-subtitle">Tap any day to log meals or review your daily breakdown.</p>
      </div>

      {/* ── Quick Stats ── */}
      <div className="stats-grid animate-in" style={{ marginBottom: 22, animationDelay: '0.08s' }}>
        <StatCard
          label="Days logged"
          value={loggedDays}
          unit="this month"
          color="#a3e635"
          icon="📅"
        />
        <StatCard
          label="Avg calories"
          value={avgCalories}
          unit="per day"
          color="#818cf8"
          icon="🔥"
        />
        <StatCard
          label="Healthy days"
          value={healthyDays}
          unit="green score"
          color="#4ade80"
          icon="💚"
        />
      </div>

      {/* ── Today Quick Access (only when viewing current month) ── */}
      {isTodayInView && (
        <button
          className="today-card animate-in"
          style={{ animationDelay: '0.14s', width: '100%', textAlign: 'left', border: 'none' }}
          onClick={() => loadDay(todayStr)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12, flexShrink: 0,
              background: 'rgba(163,230,53,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}>
              📍
            </div>
            <div>
              <div style={{
                fontSize: 13, fontWeight: 700, color: '#f1f5f9',
                fontFamily: 'Inter, sans-serif', marginBottom: 2,
              }}>
                Jump to today
              </div>
              <div style={{ fontSize: 12, color: 'rgba(241,245,249,0.45)', fontFamily: 'Inter, sans-serif' }}>
                {todayLog
                  ? `${todayLog.totalCalories} kcal logged · Health score ${todayLog.healthScore}/100`
                  : 'No meals logged yet today'}
              </div>
            </div>
          </div>
          <div style={{
            color: '#a3e635', fontSize: 20, flexShrink: 0,
            fontFamily: 'Inter, sans-serif',
          }}>
            ›
          </div>
        </button>
      )}

      {/* ── Calendar ── */}
      <div className="animate-in" style={{ animationDelay: '0.18s' }}>
        {loading ? (
          <LoadingCalendar />
        ) : (
          <NutritionCalendar
            year={year}
            month={month}
            logs={logs}
            onSelectDay={loadDay}
            onChangeMonth={handleChangeMonth}
          />
        )}
      </div>

      {/* ── Day Detail Panel ── */}
      {selectedDate && (
        <div className="animate-slide" style={{ marginTop: 20 }}>
          <DayDetail
            date={selectedDate}
            meals={dayMeals}
            dailyLog={dayLog}
            onClose={() => setSelectedDate(null)}
            onMealDeleted={handleMealDeleted}
          />
          <MealForm date={selectedDate} onMealAdded={handleMealAdded} />
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ── */

function StatCard({ label, value, unit, color, icon }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-unit">{unit}</div>
    </div>
  );
}

function LoadingCalendar() {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 20, padding: '60px 24px',
      textAlign: 'center', color: 'rgba(241,245,249,0.3)',
      fontSize: 14, fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
      Loading calendar…
    </div>
  );
}
